// conversationPreview — what a chat LIST row says happened last, for the
// `conversations` threads (order chat + direct messages).
//
// Umer, 2026-09-14 (contract 5b): "every list (group rooms, direct chats, order
// chats; web AND mobile) shows the last thing that happened — last message
// text, or `Name: Reacted 😂 to "…"` — exactly as the group list does today."
//
// The group list is the reference implementation, so the strings here are the
// group strings, character for character:
//   • body        → services/roomActions.ts `last_message_body`
//                   (`Reacted ${emoji} to “${snippet}”`, curly quotes, 40-char
//                   snippet, `📷 Photo` for a bodyless image)
//   • the row     → components/RoomsList.tsx (`${senderName}: ${body}`)
// Getting them from one module is the point: a second "Reacted…" string
// somewhere else is how the two lists drift apart again.
//
// Pure — no supabase, no React — so the format is unit-testable and the write
// path (services/conversationReactions.ts) and the read path (the Messages
// lists) cannot disagree about what a reaction looks like.

/** Longest message excerpt quoted back inside a reaction preview (group parity). */
export const PREVIEW_SNIPPET_MAX = 40;

/** The preview fields the app keeps on `conversations.meta` (epoch-ms numbers —
 *  the faithful port of the Firestore conversation doc). */
export interface ConversationPreviewMeta {
  lastMessageAt?: number;
  lastSenderId?: string;
  lastSenderName?: string;
  lastMessageBody?: string;
  lastMessageChannel?: string;
  lastMessagePerChannel?: Record<string, number>;
  lastSenderPerChannel?: Record<string, string>;
  [k: string]: unknown;
}

/**
 * The excerpt a reaction preview quotes: the message's own text, or the photo
 * placeholder when it had none, clipped to PREVIEW_SNIPPET_MAX.
 *
 * Kept identical to the RoomChat call site (`(msg.body || '📷 Photo').slice(0, 40)`)
 * so a reaction reads the same in the Groups list and the Direct list.
 */
export function previewSnippet(body: string | null | undefined): string {
  const text = (body ?? '').trim();
  return text ? text.slice(0, PREVIEW_SNIPPET_MAX) : '📷 Photo';
}

/** `Reacted 😂 to “some message”` — the body a reaction writes into the thread
 *  preview. Mirrors services/roomActions.ts verbatim (curly quotes included). */
export function reactionPreviewBody(emoji: string, snippet: string): string {
  return `Reacted ${emoji} to “${snippet}”`;
}

/**
 * The line a chat-list row shows: `Name: what happened`.
 *
 * Mirrors components/RoomsList.tsx — the sender prefix is dropped when there is
 * no name (an inbound customer SMS has none), and an empty body yields an empty
 * string so the caller can fall back to its own subtitle (the order address,
 * the user's role) rather than print a bare "Sara: ".
 */
export function chatListPreview(meta: ConversationPreviewMeta | null | undefined): string {
  const body = (meta?.lastMessageBody ?? '').trim();
  if (!body) return '';
  const sender = (meta?.lastSenderName ?? '').trim();
  return sender ? `${sender}: ${body}` : body;
}

/**
 * The same line, split for the row: when the LAST thing was mine, WhatsApp
 * shows a tick instead of my own name (`✓ On my way`, not `Umer: On my way`).
 * `mine` tells the row to draw the tick; `text` already has the prefix dropped.
 * Groups (components/RoomsList.tsx) render the identical shape off
 * `rooms.last_message_sender_id`.
 */
export function chatListPreviewParts(
  meta: ConversationPreviewMeta | null | undefined,
  viewerId: string | undefined,
): { mine: boolean; text: string } {
  const body = (meta?.lastMessageBody ?? '').trim();
  if (!body) return { mine: false, text: '' };
  const mine = !!viewerId && !!meta?.lastSenderId && meta.lastSenderId === viewerId;
  if (mine) return { mine: true, text: body };
  return { mine: false, text: chatListPreview(meta) };
}

/**
 * Merge one "something happened" event into conversations.meta, preserving
 * every other key.
 *
 * This is exactly the shape OrderChat's `supaTouchConversation` has always
 * written (top-level preview + the two per-channel maps the unread logic in
 * NotificationHandler reads). Reactions bump it through here too, so a reaction
 * moves the thread up the list and shows its preview the same way a message
 * does — which is what makes item 5b true for DMs.
 */
export function buildConversationMetaPatch(
  meta: ConversationPreviewMeta | null | undefined,
  event: { channel: string; senderId: string; senderName: string; body: string; atMs: number },
): ConversationPreviewMeta {
  const base: ConversationPreviewMeta = { ...(meta || {}) };
  return {
    ...base,
    lastMessageAt: event.atMs,
    lastSenderId: event.senderId,
    lastSenderName: event.senderName,
    lastMessageBody: event.body,
    lastMessageChannel: event.channel,
    lastMessagePerChannel: { ...(base.lastMessagePerChannel || {}), [event.channel]: event.atMs },
    lastSenderPerChannel: { ...(base.lastSenderPerChannel || {}), [event.channel]: event.senderId },
  };
}
